import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import LanguageSwitcher from "./LanguageSwitcher";
import { Trans, useTranslation } from "react-i18next";
import ToggleColorMode from "theme/ToggleColorMode";
import { useLocation } from "react-router-dom";
import TermsContent from "components/policies/TermsContent";
import PrivacyContent from "components/policies/PrivacyContent";

type DialogView = "consent" | "terms" | "privacy";

const GdprDialog: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<DialogView>("consent");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const warningRef = useRef<HTMLDivElement>(null);
  const tosCheckboxRef = useRef<HTMLInputElement | null>(null);
  const consentContainerRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const [fixedHeight, setFixedHeight] = useState<number | null>(null);

  const GDPR_ACCEPTED_KEY = "gdpr.accepted";
  const GDPR_MODE_KEY = "gdpr.mode";

  const focusFirstFormField = useCallback(() => {
    const path = location.pathname;
    const selectors: Array<{ match: (pathname: string) => boolean; selector: string }> = [
      { match: (pathname) => pathname.startsWith("/login"), selector: '[data-testid="auth-email"]' },
      {
        match: (pathname) => pathname.startsWith("/register"),
        selector: '[data-testid="signup-email"]',
      },
    ];

    const targetSelector = selectors.find((entry) => entry.match(path))?.selector;

    if (!targetSelector) {
      return;
    }

    window.requestAnimationFrame(() => {
      const element = document.querySelector<HTMLElement>(targetSelector);
      element?.focus();
    });
  }, [location.pathname]);

  useEffect(() => {
    const hasAccepted = localStorage.getItem(GDPR_ACCEPTED_KEY) === "true";

    if (hasAccepted) {
      setOpen(false);
      return;
    }

    const legacyNecessary = localStorage.getItem("gdprNecessaryAccepted") === "true";
    const legacyAll = localStorage.getItem("gdprAllAccepted") === "true";

    if (legacyNecessary || legacyAll) {
      localStorage.setItem(GDPR_ACCEPTED_KEY, "true");
      localStorage.setItem(GDPR_MODE_KEY, legacyAll ? "all" : "essential");
      setOpen(false);
      return;
    }

    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setView("consent");

    const timeout = window.requestAnimationFrame(() => {
      tosCheckboxRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(timeout);
  }, [open]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!open || view !== "consent") {
      return;
    }

    const element = consentContainerRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      const { height } = element.getBoundingClientRect();
      if (height) {
        setMeasuredHeight(Math.ceil(height));
      }
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [open, view, termsAccepted, showWarning]);

  useEffect(() => {
    if (view !== "consent" && measuredHeight) {
      setFixedHeight(measuredHeight);
    }
    if (view === "consent") {
      setFixedHeight(null);
    }
  }, [view, measuredHeight]);

  useEffect(() => {
    if (!open) {
      setMeasuredHeight(null);
      setFixedHeight(null);
    }
  }, [open]);

  const scrollToWarning = () => {
    if (warningRef.current) {
      warningRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const persistAcceptance = (mode: "essential" | "all") => {
    localStorage.setItem(GDPR_ACCEPTED_KEY, "true");
    localStorage.setItem(GDPR_MODE_KEY, mode);
    if (mode === "essential") {
      localStorage.setItem("gdprNecessaryAccepted", "true");
      localStorage.removeItem("gdprAllAccepted");
    } else {
      localStorage.setItem("gdprAllAccepted", "true");
      localStorage.removeItem("gdprNecessaryAccepted");
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("gdpr:mode-change", { detail: { mode } }));
    }
    setOpen(false);
    focusFirstFormField();
  };

  const handleAcceptNecessary = () => {
    if (!termsAccepted) {
      setShowWarning(true);
      scrollToWarning();
      return;
    }

    persistAcceptance("essential");

    window.gtag?.("consent", "update", {
      ad_user_data: "denied",
      ad_personalization: "denied",
      ad_storage: "denied",
      analytics_storage: "granted",
    });
  };

  const handleAcceptAll = () => {
    if (!termsAccepted) {
      setShowWarning(true);
      scrollToWarning();
      return;
    }

    persistAcceptance("all");

    window.gtag?.("consent", "update", {
      ad_user_data: "granted",
      ad_personalization: "granted",
      ad_storage: "granted",
      analytics_storage: "granted",
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);
    setShowWarning(false);
  };

  const handleShowTerms = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    setView("terms");
  };

  const handleShowPrivacy = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    setView("privacy");
  };

  if (!open) {
    return null;
  }

  const dialogTitle = view === "terms" ? t("termsOfService") : view === "privacy" ? t("privacyPolicy") : t("welcome");

  return (
    <Dialog
      open={open}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-modal-title"
      disableEscapeKeyDown
      data-testid="onboarding-modal"
      slotProps={{
        paper: { sx: { p: 2, borderRadius: "21px", maxWidth: "760px", width: "100%" } },
      }}
    >
      <DialogTitle id="onboarding-modal-title" variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {view !== "consent" && (
          <IconButton
            aria-label={t("back", { defaultValue: "Back" })}
            onClick={() => setView("consent")}
            size="small"
            sx={{ mr: 1 }}
          >
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
        )}
        {dialogTitle}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          position: "relative",
          height: view === "consent" ? measuredHeight ?? undefined : fixedHeight ?? measuredHeight ?? 360,
          minHeight: view === "consent" ? measuredHeight ?? undefined : fixedHeight ?? measuredHeight ?? 360,
          ...(view === "consent"
            ? measuredHeight
              ? { maxHeight: measuredHeight }
              : {}
            : { maxHeight: fixedHeight ?? measuredHeight ?? 360 }),
          overflow: "hidden",
          px: { xs: 1, sm: 2.5 },
        }}
      >
        <Fade in={view === "consent"} timeout={200} mountOnEnter unmountOnExit>
          <Box
            ref={consentContainerRef}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("selectLanguage")}
              </Typography>
              <LanguageSwitcher useButtons />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("changeApplicationTheme")}
              </Typography>
              <ToggleColorMode useButtons />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("termsOfService")}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t("gdprFadpDescription")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("kycAdditionalNotice")}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleCheckboxChange}
                  slotProps={{
                    input: { ref: tosCheckboxRef, "data-testid": "tos-checkbox" } as any,
                  }}
                />
              }
              label={
                <Typography component="span" variant="body2">
                  <Trans
                    i18nKey="acceptTerms"
                    components={[
                      <Link
                        component="button"
                        type="button"
                        onClick={handleShowTerms}
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                      />, // Terms link
                      <Link
                        component="button"
                        type="button"
                        onClick={handleShowPrivacy}
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                      />, // Privacy link
                    ]}
                  />
                </Typography>
              }
            />
            {showWarning && (
              <div ref={warningRef}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t("termsWarning")}
                </Alert>
              </div>
            )}
          </Box>
        </Fade>

        <Fade in={view === "terms"} timeout={200} mountOnEnter unmountOnExit>
          <Box sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: { xs: 1, sm: 2 } }}>
            <TermsContent size="compact" />
          </Box>
        </Fade>

        <Fade in={view === "privacy"} timeout={200} mountOnEnter unmountOnExit>
          <Box sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: { xs: 1, sm: 2 } }}>
            <PrivacyContent size="compact" />
          </Box>
        </Fade>
      </DialogContent>
      {view === "consent" && (
        <DialogActions>
          <Button onClick={handleAcceptNecessary} color="primary" variant="outlined" data-testid="accept-essential">
            {t("acceptNecessary")}
          </Button>
          <Button onClick={handleAcceptAll} color="primary" variant="contained" data-testid="accept-all">
            {t("acceptAll")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GdprDialog;
